import logger from '@/lib/logging';
/**
 * Tag System JSON Import/Export Handlers
 * Handles import and export of RuleDoc-Drill-Taxonomy.json
 */

const hasLocalStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export interface TagTreeJSON {
  version: string;
  lastModified: string;
  categories: {
    [key: string]: {
      type: string;
      description?: string;
      options: string[];
    };
  };
  defaults?: {
    colorizationCategory?: string;
  };
  subcategoryMap?: {
    [key: string]: string[];
  };
  programMethodology?: {
    [key: string]: string;
  };
  colors: {
    categoryLevelColors: {
      [key: string]: string; // Color for entire category (HEX format)
    };
    optionColors: {
      [key: string]: string; // Color for individual options (HEX format, overrides category)
    };
  };
}

export interface TagOption {
  label: string;
  description?: string;
  color?: string; // Optional override color in HEX format
}

export const TAXONOMY_STORAGE_KEY = 'drillTaxonomy_json';
export const LEGACY_TAXONOMY_STORAGE_KEYS = ['tagTree_json'];

export function writeTaxonomyToStorage(content: string) {
  if (!hasLocalStorage()) return;
  localStorage.setItem(TAXONOMY_STORAGE_KEY, content);
  LEGACY_TAXONOMY_STORAGE_KEYS.forEach((legacyKey) => {
    if (legacyKey !== TAXONOMY_STORAGE_KEY) {
      localStorage.removeItem(legacyKey);
    }
  });
}

export function readTaxonomyFromStorage(): string | null {
  if (!hasLocalStorage()) return null;

  const current = localStorage.getItem(TAXONOMY_STORAGE_KEY);
  if (current) {
    return current;
  }

  for (const legacyKey of LEGACY_TAXONOMY_STORAGE_KEYS) {
    const legacyValue = localStorage.getItem(legacyKey);
    if (!legacyValue) continue;

    try {
      JSON.parse(legacyValue);
      writeTaxonomyToStorage(legacyValue);
      return legacyValue;
    } catch (error) {
      logger.warn(`Invalid taxonomy data in legacy storage key "${legacyKey}"`, error);
      localStorage.removeItem(legacyKey);
    }
  }

  return null;
}

/**
 * Export tag tree as comprehensive taxonomy JSON file
 * Includes all tags, categories, descriptions, and colors in HEX format
 */
export async function exportTagTree(): Promise<void> {
  try {
    // Load tag tree
    const tagTree = await loadTagTree();
    
    // Build comprehensive export with all tag information
    const comprehensiveExport = buildComprehensiveTagTree(tagTree);
    
    // Create formatted JSON
    const content = JSON.stringify(comprehensiveExport, null, 2);
    
    // Download file
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `RuleDoc-Drill-Taxonomy-${Date.now()}.json`;
    link.click();
    
    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    logger.error('Failed to export tag tree:', error);
    throw error;
  }
}

/**
 * Build comprehensive tag tree with all information
 */
function buildComprehensiveTagTree(tagTree: TagTreeJSON): TagTreeJSON {
  return {
    version: tagTree.version || '1.0.0',
    lastModified: new Date().toISOString(),
    categories: tagTree.categories,
    defaults: tagTree.defaults,
    subcategoryMap: tagTree.subcategoryMap,
    programMethodology: tagTree.programMethodology,
    colors: {
      categoryLevelColors: tagTree.colors.categoryLevelColors,
      optionColors: tagTree.colors.optionColors
    }
  };
}

/**
 * Import tag tree from JSON file
 */
export async function importTagTree(file: File): Promise<{success: boolean, error?: string, warnings?: string[]}> {
  try {
    const content = await file.text();

    let rawJson: unknown;
    try {
      rawJson = JSON.parse(content);
    } catch (e) {
      return { success: false, error: 'Invalid JSON format. Please check the file syntax.' };
    }

    const normalizeResult = normalizeImportedTagTree(rawJson);
    if (!normalizeResult.success || !normalizeResult.tree) {
      const errorMessage = normalizeResult.errors?.length
        ? normalizeResult.errors.join('\n')
        : 'Failed to normalize imported tag tree.';
      return { success: false, error: errorMessage };
    }

    const normalizedTree = normalizeResult.tree;
    normalizedTree.lastModified = new Date().toISOString();

    writeTaxonomyToStorage(JSON.stringify(normalizedTree, null, 2));

    if (normalizeResult.warnings?.length) {
      logger.warn('Tag tree import warnings:', normalizeResult.warnings);
    }

    return { success: true, warnings: normalizeResult.warnings };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

const ALLOWED_CATEGORY_TYPES = new Set(['select', 'checkbox']);
const HEX_COLOR_REGEX = /^#[0-9A-F]{6}$/i;

type NormalizeResult = {
  success: false;
  errors: string[];
  warnings?: string[];
} | {
  success: true;
  tree: TagTreeJSON;
  warnings: string[];
  errors?: string[];
};

function normalizeImportedTagTree(rawJson: unknown): NormalizeResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!rawJson || typeof rawJson !== 'object' || Array.isArray(rawJson)) {
    return { success: false, errors: ['Root JSON must be an object.'] };
  }

  const raw = rawJson as Partial<TagTreeJSON> & { [key: string]: any };

  if (!raw.categories || typeof raw.categories !== 'object' || Array.isArray(raw.categories)) {
    return { success: false, errors: ['Missing or invalid "categories" object.'] };
  }

  const normalizedCategories: TagTreeJSON['categories'] = {};
  const categoryKeyMap = new Map<string, string>();
  const optionLabels = new Set<string>();
  const optionColorOverrides = new Map<string, string>();

  for (const [rawKey, rawValue] of Object.entries(raw.categories)) {
    if (!rawKey || typeof rawKey !== 'string') {
      errors.push('Encountered a category name that is not a string.');
      continue;
    }

    const trimmedKey = rawKey.trim();
    if (!trimmedKey) {
      errors.push('Encountered a category name that is empty after trimming.');
      continue;
    }

    if (normalizedCategories[trimmedKey]) {
      errors.push(`Duplicate category name detected: "${trimmedKey}".`);
      continue;
    }

    categoryKeyMap.set(rawKey, trimmedKey);

    const value = rawValue || {};
    const rawType = typeof value.type === 'string' ? value.type.trim().toLowerCase() : 'select';
    const normalizedType = ALLOWED_CATEGORY_TYPES.has(rawType) ? rawType : 'select';

    if (!ALLOWED_CATEGORY_TYPES.has(rawType)) {
      warnings.push(`Unsupported category type "${value.type}" for "${trimmedKey}". Defaulting to "select".`);
    }

    if (!Array.isArray(value.options)) {
      errors.push(`Invalid options for "${trimmedKey}". Expected an array.`);
      continue;
    }

    const optionSet = new Set<string>();
    const normalizedOptions: string[] = [];

    value.options.forEach((option: any, index: number) => {
      let label = '';

      if (typeof option === 'string') {
        label = option.trim();
      } else if (option && typeof option === 'object') {
        label = typeof option.label === 'string' ? option.label.trim() : '';

        if (!label) {
          errors.push(`Option at index ${index} in category "${trimmedKey}" is missing a valid label.`);
          return;
        }

        if (option.color) {
          const normalizedColor = normalizeHexColor(option.color, `categories.${trimmedKey}.options.${label}.color`, errors, warnings);
          if (normalizedColor) {
            optionColorOverrides.set(label, normalizedColor);
          }
        }
      } else {
        errors.push(`Invalid option format in "${trimmedKey}" at index ${index}.`);
        return;
      }

      if (!label) {
        errors.push(`Option at index ${index} in category "${trimmedKey}" is empty.`);
        return;
      }

      if (!optionSet.has(label)) {
        optionSet.add(label);
        normalizedOptions.push(label);
        optionLabels.add(label);
      }
    });

    normalizedCategories[trimmedKey] = {
      type: normalizedType,
      description: typeof value.description === 'string' ? value.description : undefined,
      options: normalizedOptions
    };
  }

  if (Object.keys(normalizedCategories).length === 0) {
    errors.push('No valid categories found in the imported file.');
  }

  const normalizedCategoryColors: Record<string, string> = {};
  const normalizedOptionColors: Record<string, string> = {};

  const rawColors: any = raw.colors && typeof raw.colors === 'object' ? raw.colors : {};
  const rawCategoryColors = rawColors && typeof rawColors.categoryLevelColors === 'object' && !Array.isArray(rawColors.categoryLevelColors)
    ? rawColors.categoryLevelColors
    : {};
  const rawOptionColors = rawColors && typeof rawColors.optionColors === 'object' && !Array.isArray(rawColors.optionColors)
    ? rawColors.optionColors
    : {};

  for (const [rawKey, colorValue] of Object.entries(rawCategoryColors as Record<string, any>)) {
    const mappedKey = categoryKeyMap.get(rawKey) || rawKey.trim();
    if (!mappedKey || !normalizedCategories[mappedKey]) {
      warnings.push(`Category color assigned to "${rawKey}" does not match any imported category and was ignored.`);
      continue;
    }

    const normalizedColor = normalizeHexColor(colorValue, `colors.categoryLevelColors.${rawKey}`, errors, warnings);
    if (normalizedColor) {
      normalizedCategoryColors[mappedKey] = normalizedColor;
    }
  }

  for (const [rawKey, colorValue] of Object.entries(rawOptionColors as Record<string, any>)) {
    const trimmedKey = typeof rawKey === 'string' ? rawKey.trim() : '';
    if (!trimmedKey) continue;
    if (!optionLabels.has(trimmedKey)) {
      warnings.push(`Option color assigned to "${rawKey}" does not match any option and was ignored.`);
      continue;
    }

    const normalizedColor = normalizeHexColor(colorValue, `colors.optionColors.${rawKey}`, errors, warnings);
    if (normalizedColor) {
      normalizedOptionColors[trimmedKey] = normalizedColor;
    }
  }

  optionColorOverrides.forEach((color, optionLabel) => {
    normalizedOptionColors[optionLabel] = color;
  });

  let normalizedSubcategoryMap: TagTreeJSON['subcategoryMap'];
  if (raw.subcategoryMap && typeof raw.subcategoryMap === 'object' && !Array.isArray(raw.subcategoryMap)) {
    const mapEntries: Record<string, string[]> = {};
    for (const [domain, list] of Object.entries(raw.subcategoryMap as Record<string, unknown>)) {
      if (!Array.isArray(list)) {
        warnings.push(`Subcategory list for "${domain}" is not an array and was ignored.`);
        continue;
      }
      const cleaned = Array.from(new Set(list
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)));
      if (cleaned.length > 0) {
        mapEntries[domain.trim() || domain] = cleaned;
      }
    }
    if (Object.keys(mapEntries).length > 0) {
      normalizedSubcategoryMap = mapEntries;
    }
  }

  let normalizedProgramMethodology: TagTreeJSON['programMethodology'];
  if (raw.programMethodology && typeof raw.programMethodology === 'object' && !Array.isArray(raw.programMethodology)) {
    const methodologyEntries: Record<string, string> = {};
    for (const [key, value] of Object.entries(raw.programMethodology as Record<string, unknown>)) {
      if (typeof value !== 'string') {
        warnings.push(`Program methodology entry for "${key}" is not a string and was ignored.`);
        continue;
      }
      const trimmedKey = key.trim();
      methodologyEntries[trimmedKey || key] = value;
    }
    if (Object.keys(methodologyEntries).length > 0) {
      normalizedProgramMethodology = methodologyEntries;
    }
  }

  let normalizedDefaults: TagTreeJSON['defaults'];
  if (raw.defaults && typeof raw.defaults === 'object' && !Array.isArray(raw.defaults)) {
    const defaults: Record<string, unknown> = raw.defaults;
    const colorizationValue = defaults.colorizationCategory;
    if (typeof colorizationValue === 'string' && colorizationValue.trim()) {
      normalizedDefaults = {
        colorizationCategory: colorizationValue.trim()
      };
    } else if (colorizationValue !== undefined) {
      warnings.push('defaults.colorizationCategory must be a non-empty string; value was ignored.');
    }
  }

  if (errors.length >= 15) {
    return {
      success: false,
      errors: errors.slice(0, 15).concat(`...and ${errors.length - 15} more issues.`),
      warnings
    };
  }

  const version = typeof raw.version === 'string' && raw.version.trim() ? raw.version.trim() : '1.1.0';

  const normalizedTree: TagTreeJSON = {
    version,
    lastModified: new Date().toISOString(),
    categories: normalizedCategories,
    defaults: normalizedDefaults,
    subcategoryMap: normalizedSubcategoryMap,
    programMethodology: normalizedProgramMethodology,
    colors: {
      categoryLevelColors: normalizedCategoryColors,
      optionColors: normalizedOptionColors
    }
  };

  if (errors.length > 0) {
    return { success: false, errors, warnings };
  }

  return { success: true, tree: normalizedTree, warnings };
}

function normalizeHexColor(value: unknown, fieldName: string, errors: string[], warnings: string[]): string | null {
  if (typeof value !== 'string') {
    warnings.push(`Color value for ${fieldName} is not a string and was ignored.`);
    return null;
  }

  let normalized = value.trim();
  if (!normalized) {
    warnings.push(`Color value for ${fieldName} is empty and was ignored.`);
    return null;
  }

  if (!normalized.startsWith('#')) {
    normalized = `#${normalized}`;
  }

  if (!HEX_COLOR_REGEX.test(normalized)) {
    warnings.push(`Invalid HEX color in ${fieldName}: "${value}". Expected #RRGGBB. The value was ignored.`);
    return null;
  }

  return normalized.toUpperCase();
}

/**
 * Load tag tree from localStorage or default
 */
export async function loadTagTree(): Promise<TagTreeJSON> {
  const stored = readTaxonomyFromStorage();

  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      logger.error('Failed to parse stored tag tree, loading default', e);
    }
  }

  // Load default
  const module = await import('@/config/RuleDoc-Drill-Taxonomy.json');
  return module.default as unknown as TagTreeJSON;
}

/**
 * Get last modified date for tag tree
 */
export async function getTagTreeLastModified(): Promise<string> {
  try {
    const tagTree = await loadTagTree();
    return tagTree.lastModified || 'Never';
  } catch (e) {
    return 'Never';
  }
}

